import { CircleQuestionMark } from "lucide-react";
import type { ComponentProps } from "react";
import { useId, useRef } from "react";

import type { ServiceEntity } from "~/entities/service";
import type { CalculatorFormHandle } from "~/features/service";
import { CalculatorForm, ServiceDetailsDialog } from "~/features/service";
import { bounded } from "~/shared/fp";
import { Tk, TranslationParams, useTranslation2 } from "~/shared/i18n";
import { useControllableState } from "~/shared/react";
import { Button } from "~/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shared/ui/dialog";

type Props = ComponentProps<typeof Dialog> & {
  services: ServiceEntity.Schema[];
  triggerProps?:
    | (Required<Pick<ComponentProps<typeof DialogTrigger>, "children">> &
        Omit<ComponentProps<typeof DialogTrigger>, "children">)
    | undefined;
  okButtonT?: TranslationParams.Type | undefined;
  disableFooterButtons?: boolean | undefined;
} & Pick<ComponentProps<typeof CalculatorForm>, "orderedServices"> &
  Pick<ComponentProps<typeof CalculatorForm>, "onSubmit">;

function ServiceSelectionDialog({
  services,
  triggerProps,
  open,
  okButtonT,
  disableFooterButtons,
  orderedServices,
  onSubmit,
  ...props
}: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  const [isOpen, handleOpenChange] = useControllableState({
    defaultValue: false,
    value: open,
    onChange: bounded(props, "onOpenChange"),
  });

  const formId = useId();
  const formRef = useRef<CalculatorFormHandle>(null);

  const { asChild: triggerAsChild = true, ...triggerProps_ } = triggerProps ?? {};

  return (
    <Dialog open={isOpen} {...props} onOpenChange={handleOpenChange}>
      {triggerProps_ && <DialogTrigger asChild={triggerAsChild} {...triggerProps_} />}
      <DialogContent
        className="flex max-h-4/5 flex-col md:max-w-2xl lg:max-w-4xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{tc(Tk.EDITING_ORDERED_SERVICES)}</DialogTitle>
        </DialogHeader>
        <CalculatorForm
          id={formId}
          ref={formRef}
          orderedServices={orderedServices}
          services={services}
          moreInfoButton={(service) => (
            <ServiceDetailsDialog
              service={service}
              triggerProps={{
                asChild: true,
                children: (
                  <Button variant="ghost" size="auto" className="size-6">
                    <CircleQuestionMark />
                  </Button>
                ),
              }}
            />
          )}
          onSubmit={onSubmit}
          className="overflow-auto"
        />
        <DialogFooter>
          <Button
            variant="outline"
            disabled={disableFooterButtons}
            onClick={() => formRef.current?.hookForm.reset()}
          >
            {tc(Tk.RESET)}
          </Button>
          <Button disabled={disableFooterButtons} form={formId}>
            {okButtonT ? tc(...TranslationParams.toTuple(okButtonT)) : tc(Tk.SAVE)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ServiceSelectionDialog };
